import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import apiClient from "utils/apiClient";

export default function StatusUpdateModal({
  show,
  onClose,
  user,
  loggedInUserId,
  onStatusUpdated,
}) {
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState(user?.statusChangeNotes || "");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    setNewStatus("");
    setStatusNote(user?.statusChangeNotes || "");
  }, [user]);

  const requiresNote = useMemo(
    () => ["SUSPENDED", "DELETED"].includes(newStatus),
    [newStatus],
  );
  const trimmedStatusNote = statusNote.trim();
  const existingStatusNote = (user?.statusChangeNotes || "").trim();
  const statusChanged = !!newStatus && newStatus !== user.status;
  const noteChanged = trimmedStatusNote !== existingStatusNote;

  const handleUpdateStatus = async () => {
    if (loggedInUserId === user.id) {
      toast.error("You cannot change your own account status.");
      return;
    }

    const forbiddenTransitions = {
      DELETED: ["ACTIVE"],
    };

    if (statusChanged && forbiddenTransitions[user.status]?.includes(newStatus)) {
      toast.error("Invalid status transition.");
      return;
    }

    if (!statusChanged && !noteChanged) return;

    if (statusChanged && requiresNote && !trimmedStatusNote) {
      toast.error("Please provide a note for this status change.");
      return;
    }

    try {
      setUpdatingStatus(true);
      const payload = [
        { name: "statusChangeNotes", value: trimmedStatusNote },
      ];

      if (statusChanged) {
        payload.unshift({ name: "status", value: newStatus });
      }

      await apiClient.patch(
        `/identity-service/api/v1/users/props?uid=${user.id}`,
        payload,
      );

      onStatusUpdated({
        status: statusChanged ? newStatus : user.status,
        note: trimmedStatusNote,
      });
      toast.success("User status updated successfully");
      onClose();
    } catch (err) {
      toast.error("Failed to update user status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleClose = () => {
    setNewStatus("");
    setStatusNote(user?.statusChangeNotes || "");
    onClose();
  }

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="text-sm">Update Account Status</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-xs mb-3">
          Change account status for{" "}
          <strong>
            {user.firstName} {user.lastName}
          </strong>
        </p>

        <label className="form-label text-xxs">New Status</label>
        <select
          className="form-select form-select-sm"
          value={newStatus || ""}
          onChange={(e) => {
            const selected = e.target.value;
            setNewStatus(selected);
            if (!["SUSPENDED", "DELETED"].includes(selected)) {
              setStatusNote("");
            }
          }}
        >
          <option value="" disabled>
            Select option
          </option>
          <option value="ACTIVE">ACTIVE</option>
          <option value="SUSPENDED">SUSPENDED</option>
          <option value="DISABLED">DISABLED</option>
          <option value="DELETED">DELETED</option>
        </select>

        {newStatus === "DELETED" && (
          <div className="alert alert-warning text-xs mt-3 mb-0">
            ⚠️ This action may disable user access immediately.
          </div>
        )}

        <label className="form-label text-xxs mt-3">
          Status Update Notes{" "}
          {requiresNote && <span className="text-danger">*</span>}
        </label>
        <textarea
          className={`form-control form-control-sm ${
            requiresNote && !statusNote.trim() ? "is-invalid" : ""
          }`}
          rows={3}
          placeholder={
            requiresNote
              ? "Please explain why this status is being changed..."
              : "Optional note for this status update"
          }
          value={statusNote}
          onChange={(e) => setStatusNote(e.target.value)}
          readOnly={["SUSPENDED", "DELETED"].includes(user?.status)}
        />
        {requiresNote && !statusNote.trim() && (
          <div className="invalid-feedback text-xxs">
            A note is required when suspending or deleting an account.
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0">
        <Button
          size="sm"
          variant="secondary"
          onClick={handleClose}
          disabled={updatingStatus}
        >
          Cancel
        </Button>

        <Button
          size="sm"
          variant="success"
          onClick={handleUpdateStatus}
          disabled={
            updatingStatus ||
            (!statusChanged && !noteChanged) ||
            (requiresNote && !statusNote.trim())
          }
        >
          {updatingStatus ? (
            <Spinner animation="border" size="sm" />
          ) : (
            "Confirm Update"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
